/** Class-session calendar data hooks (CS-01/CS-06). PostgREST reads + Edge Fn
 *  mutations via @gym/api-client. RLS scopes rows to the tenant. */
import {
  cancelClassSession,
  createClassSessions,
  listClasses,
  listClassSessions,
  patchClassSession,
} from '@gym/api-client';
import type { schemas, types } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../../lib/supabase.js';

export interface SessionRangeFilter {
  from: string;
  to: string;
  locationId?: string;
  onlyPt?: boolean;
  instructorStaffId?: string;
}

function mapSession(r: Record<string, unknown>): types.ClassSessionDTO {
  return {
    id: r['id'] as string,
    tenantId: r['tenant_id'] as string,
    classId: (r['class_id'] as string | null) ?? null,
    locationId: r['location_id'] as string,
    instructorStaffId: (r['instructor_staff_id'] as string | null) ?? null,
    startsAt: r['starts_at'] as string,
    endsAt: r['ends_at'] as string,
    capacity: r['capacity'] as number,
    bookedCount: (r['booked_count'] as number) ?? 0,
    waitlistCount: (r['waitlist_count'] as number) ?? 0,
    room: (r['room'] as string | null) ?? null,
    status: r['status'] as types.ClassSessionDTO['status'],
    bookingOpensAt: (r['booking_opens_at'] as string | null) ?? null,
    bookingClosesAt: (r['booking_closes_at'] as string | null) ?? null,
    cancelCutoffAt: (r['cancel_cutoff_at'] as string | null) ?? null,
    isPt: (r['is_pt'] as boolean) ?? false,
  };
}

export function useClassSessions(filter: SessionRangeFilter) {
  return useQuery({
    queryKey: ['class_sessions', filter],
    queryFn: async (): Promise<types.ClassSessionDTO[]> => {
      let q = listClassSessions(supabase, {
        from: filter.from,
        to: filter.to,
        ...(filter.locationId ? { locationId: filter.locationId } : {}),
        ...(filter.onlyPt !== undefined ? { onlyPt: filter.onlyPt } : {}),
      });
      if (filter.instructorStaffId) q = q.eq('instructor_staff_id', filter.instructorStaffId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((r) => mapSession(r as Record<string, unknown>));
    },
    staleTime: 60_000,
  });
}

export interface ClassTemplate {
  id: string;
  name: string;
  default_instructor_staff_id: string | null;
  room: string | null;
}

export function useClassTemplates() {
  return useQuery({
    queryKey: ['classes'],
    queryFn: async (): Promise<ClassTemplate[]> => {
      const { data, error } = await listClasses(supabase);
      if (error) throw error;
      return (data ?? []) as unknown as ClassTemplate[];
    },
    staleTime: 300_000,
  });
}

export function useCreateClassSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: schemas.ClassSessionCreateInput) => createClassSessions(supabase, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['class_sessions'] }),
  });
}

export function usePatchClassSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; input: schemas.ClassSessionPatchInput }) =>
      patchClassSession(supabase, vars.id, vars.input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['class_sessions'] }),
  });
}

export function useCancelClassSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      cancelClassSession(supabase, vars.id, vars.reason),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['class_sessions'] }),
  });
}
